<template>
  <vue-resizable :active="['r']" :min-width="($vuetify.breakpoint.mdAndUp) ? 350 : getWindowWidth"
    :max-width="($vuetify.breakpoint.mdAndUp) ? getMaxWidth : getWindowWidth" id="sidebar-container">
    <ConceptsSearch class="mb-4 height-auto concepts-browser-container" />

    <v-switch v-model="switchCompleteTree" hide-details
      class="switch-tree justify-center align-center no-select mb-4 mt-2 pa-0" @change="initTreeView">
      <template #prepend>
        <v-label>Simplifiée</v-label>
      </template>
      <template #append>
        <v-label>Complète</v-label>
      </template>
    </v-switch>

    <div id="alert-children-limit" v-if="showIncompleteNodeAlert"
      class="o-alert o-alert--info fade show alert ans-alert-banner" role="alert">
      <div class="o-alert__icon">
        <svg class="svg-icon svg-circle-info" aria-hidden="true" focusable="false">
          <use xlink:href="/wp-content/themes/ans/design-system/svg-icons/icon-sprite.svg#circle-info"></use>
        </svg>
      </div>

      <p>Affichage limité à <b>{{ childrenLimit }}</b> concepts. Utilisez la recherche pour afficher un concept
        particulier.
      </p>
    </div>

    <ConceptsTreeSkeletonLoader v-if="conceptsTreeLoading" id="concept-tree-container" />
    <template v-if="conceptsTree.length > 0 && !conceptsTreeLoading">
      <ConceptsTree v-if="Object.keys(UUID.mapping).length" :conceptsTree="conceptsTree"
        :activeConcepts="activeConcepts" @updateActive="updateActive" @loadChildren="loadChildren"
        id="concept-tree-container" />
    </template>
  </vue-resizable>
</template>

<script>
import ConceptsStore from "../concepts-store";
import StoreConfiguration from "../store-configuration";
import ConceptsSearch from "./ConceptsSearch";
import ConceptsTree from "./ConceptsTree";
import ConceptsTreeSkeletonLoader from "./ConceptsTreeSkeletonLoader";
import VueResizable from 'vue-resizable';
import TreeUtils from "../tree-utils";
import VueApp from "../main";
import Snomed from "../snomed";

export default {
  name: "ConceptsBrowser.vue",

  components: {
    ConceptsTree,
    ConceptsSearch,
    VueResizable,
    ConceptsTreeSkeletonLoader
  },

  data: () => ({
    store: ConceptsStore.state,
    storeConfiguration: StoreConfiguration.state,
    switchCompleteTree: (VueApp.$route.query.treeViewType === "complete"),
    conceptsTreeLoading: true,
    conceptsTree: [],
    activeConcepts: [],
    conceptsTreeClass: {
      conceptsTreeClassFormatted: undefined,
      conceptsId: undefined,
    },
    UUID: {
      mapping: {},
      reverseMapping: {},
      directParentUriMapping: {}
    },
    rootConcepts: [],
    childConcepts: {},
    incompleteNodes: [],
    childrenLimit: 10000,
    showIncompleteNodeAlert: false,
    shouldScrollToActiveConcept: false, // Flag pour indiquer de scroller au prochain "updated" du composant.
  }),

  computed: {
    /**
     * Get the max width of the concepts tree container.
     */
    getMaxWidth: function () {
      return window.innerWidth - 500;
    },

    /**
     * Get the max width of the concepts tree container.
     */
    getWindowWidth: function () {
      return (window.innerWidth * 0.9);
    },

    activeConceptsDirectParentsUri() {
      const parentsUri = [];
      Object.keys(this.UUID.directParentUriMapping).filter(k => this.activeConcepts.includes(k)).forEach(k => {
        parentsUri.push(this.UUID.directParentUriMapping[k]);
      })
      return parentsUri;
    },
  },

  async created() {
    await this.initTreeView();
  },

  updated: function () {
    this.$nextTick(function () {
      // Will run only after the entire view has been re-rendered
      if (this.shouldScrollToActiveConcept) {
        this.scrollToActiveConcept();
        this.shouldScrollToActiveConcept = false;
      }
    })
  },

  methods: {
    initTreeView: async function () {
      ConceptsStore.updateUrlParameters({ treeViewType: (this.switchCompleteTree) ? "complete" : "simple" });

      this.conceptsTreeLoading = true;

      this.conceptsTree = await this.generateConceptsTree();
      this.shouldScrollToActiveConcept = true;

      this.conceptsTreeLoading = false;
    },

    /**
     * Generate concepts tree depending on the display type selected : simplified or complete.
     */
    generateConceptsTree: async function () {

      if (!ConceptsStore.checkUrlParameterExists("concept")) return;

      ConceptsStore.setConceptId(this.$route.query.concept);

      // Get common variables only if they are not already defined (performance purpose)
      this.conceptsTreeClass.conceptsTreeClassFormatted = this.formatTreeClass(this.store.conceptDetail.treeClass);
      this.conceptsTreeClass.conceptsId = this.conceptsTreeClass.conceptsTreeClassFormatted.map((item) => item.id).filter((item) => item).filter((item, index, arr) => arr.indexOf(item) == index);
      if (!this.conceptsTreeClass.conceptsId.length) this.conceptsTreeClass.conceptsId = [this.store.conceptDetail.id];

      // Retrieve root and child concepts.
      this.childrenLimit = StoreConfiguration.getChildrenLimit();
      this.rootConcepts = await this.getRootConcepts();
      let parentIds = [...this.conceptsTreeClass.conceptsId];
      // if mode "complete", we should load all roots children
      if (this.switchCompleteTree != 'simple') {
        this.rootConcepts.forEach(concept => {
          if (parentIds.indexOf(concept.id) < 0) {
            parentIds.push(concept.id);
          }
        });
      }
      this.childConcepts = await this.getChildConcepts(parentIds);

      // Build common concepts tree paths for both simplified and complete tree.
      let treeClassFormatted =
        (this.switchCompleteTree)
          ? await this.populateCompleteTreeClass(this.conceptsTreeClass.conceptsTreeClassFormatted)
          : await this.populateSimplifiedTreeClass(this.conceptsTreeClass.conceptsTreeClassFormatted)
        ;

      let conceptsTreeTemp = this.buildConceptsTree(treeClassFormatted);

      // Retrieve configuration for each concept node.
      await StoreConfiguration.getDefaultConfiguration();
      conceptsTreeTemp = StoreConfiguration.initConceptsTreeConfiguration(conceptsTreeTemp);

      // Firstly sort by concept/individual, then sort by configurations
      conceptsTreeTemp = this.sortConceptTree(conceptsTreeTemp, StoreConfiguration.getConceptTreeRootConfiguration());

      this.updateShowIncompleteNodeAlert();

      return conceptsTreeTemp;
    },

    sortConceptTree(tree, configuration) {
      if (tree && tree.length) {
        this.sortTreeNode(tree, configuration);
        tree.forEach(node => {
          node.children = this.sortConceptTree(node.children, node.configuration);
        });
      }
      return tree;
    },

    /**
     * Firstly sort by concept/individual, then sort by configurations.
     * @param node
     * @param deep
     */
    sortTreeNode(children, configuration) {
      return TreeUtils.sortTreeNodesByConf(children, configuration, this.$route.query.lang ? this.$route.query.lang : 'en');
    },

    /**
     * Populate simplified tree class by retrieving concepts' label for simplified concepts tree.
     */
    populateSimplifiedTreeClass: async function (initTreeClass) {

      let treeClassRootConceptsId = initTreeClass.filter((node) => node.parentId === "").map((node) => node.id);

      // Add root concepts and remove the one already in the tree class to avoid duplicate.
      let treeClassWithRoots = [
        ...initTreeClass.filter((node) => node.parentId !== ""),
        ...this.rootConcepts.filter((node) => treeClassRootConceptsId.includes(node.id))
      ];

      return treeClassWithRoots.map((node) => {

        if (this.childConcepts[node.id] && typeof this.childConcepts[node.id] !== undefined) {

          if (this.childConcepts[node.id]?.props?.length > 0) {
            node["props"] = TreeUtils.formatConceptProps(this.childConcepts[node.id].props);
          }

          // Pour le cas du tableau vide par défaut.
          node["children"] = undefined;

          if (this.childConcepts[node.id].children?.length > 0) {

            if (node.id === this.store.conceptId) {
              node["children"] = this.childConcepts[node.id]?.children;
            } else {
              let includedChildren = treeClassWithRoots.filter(a => a.parentId === node.id).map(el => el.id);

              node["children"] = this.childConcepts[node.id]?.children?.filter(el => includedChildren.includes(el.id)) || [];
            }
          }
        }

        return node;
      });
    },

    /**
     * Populate complete tree class by retrieving root and sibling concepts in order to have label.
     */
    populateCompleteTreeClass: async function (initTreeClass) {

      // Add root concepts and remove the one already in the tree class to avoid duplicate.
      let treeClassWithRoots = [
        ...initTreeClass.filter((node) => node.parentId !== ""),
        ...this.rootConcepts
      ];

      return treeClassWithRoots.map((node) => {

        if (this.childConcepts[node.id] && typeof this.childConcepts[node.id] !== undefined) {

          if (this.childConcepts[node.id].props?.length > 0) {
            node["props"] = TreeUtils.formatConceptProps(this.childConcepts[node.id].props);
          }

          // Pour le cas du tableau vide par défaut.
          node["children"] = undefined;

          if (this.childConcepts[node.id].children?.length > 0) {
            node["children"] = this.childConcepts[node.id].children;
          }
        }

        return node;
      });
    },

    /**
     * Get all root concepts of a specific terminology.
     */
    getRootConcepts: async function () {
      await ConceptsStore.refreshUserTokenIfExpired();
      return TreeUtils.getRootConcepts(this.store.terminologyId, this.childrenLimit, Snomed.checkIfSnomedBrowerLicenceValid(), this.$route.query.lang);
    },

    /**
     * Get child concepts of a parent concept.
     */
    getChildConcepts: async function (conceptsId) {

      await ConceptsStore.refreshUserTokenIfExpired();

      let childrenId = [];

      return await TreeUtils.getChildConcepts(conceptsId, this.store.terminologyId, this.childrenLimit, Snomed.checkIfSnomedBrowerLicenceValid(), this.$route.query.lang)
        .then(async (response) => {

          // Sort results ascending depending on the "order" attribute
          Object.values(response.data).forEach(children => children.conceptSubClasses.sort((a, b) => (a.order > b.order) ? 1 : ((b.order > a.order) ? -1 : 0)));

          // Map results to retrieve only necessary fields
          for (const [key, value] of Object.entries(response.data)) {

            // Add concept ID to the list of concepts ID.
            if (!childrenId.includes(key)) { childrenId.push(key); }

            let childConceptsRefactored = value.conceptSubClasses.map((childConcept) => {

              // Add concept ID to the list of concepts ID.
              if (!childrenId.includes(childConcept.id)) { childrenId.push(childConcept.id); }

              return {
                id: childConcept.id,
                parentId: key,
                class: childConcept.class,
                isIndividual: childConcept.individual,
                ...(childConcept.props && { props: TreeUtils.formatConceptProps(childConcept.props) }),
                ...(!childConcept.leaf && { children: [] })
              };
            });

            response.data[key] = {
              children: childConceptsRefactored,
              props: value.props
            };

            let missingChildrenOfConcept = this.store.conceptDetail.treeClass.filter(v => v.parents.includes(key) && !value.conceptSubClasses.map(s => s.id).includes(v.id));
            if (missingChildrenOfConcept && missingChildrenOfConcept.length > 0) {
              missingChildrenOfConcept.forEach(n => {
                if (!response.data[key].children) {
                  response.data[key].children = [];
                }
                response.data[key].children.push({
                  id: n.id,
                  parentId: key,
                  leaf: this.store.conceptDetail.treeClass.filter(v => v.parents.includes(n.id)).length === 0,
                  class: true
                });
              });
            }

            if (value.nbChildren > value.conceptSubClasses.length) {
              this.incompleteNodes.push(key);
            }
          }

          return response.data;
        });
    },



    /**
     * Format the initial tree class to fit with the necessary format to build the concepts tree : flat object.
     */
    formatTreeClass: function (initTreeClass) {

      let treeClassFormatted = [];

      initTreeClass.map((concept) => {
        concept.parents.forEach((parentId) => {

          if (!initTreeClass.some((el) => el.id === parentId) && !treeClassFormatted.some((el) => el.id === parentId)) {
            // Detect roots node then add empty parentId for root node
            treeClassFormatted.push({ id: parentId, parentId: "" });
          }

          // Duplicate node object for each parentId detected
          treeClassFormatted.push({ id: concept.id, parentId: parentId })
        });
      });

      if (treeClassFormatted.length === 0) {
        treeClassFormatted.push({ id: this.store.conceptId, parentId: "" })
      }

      return treeClassFormatted;
    },

    /**
     * Build concepts tree from formatted tree class.
     * @param data
     * @returns []
     */
    buildConceptsTree: function (data) {

      // Map node index related to the initial data.
      let map = {};
      data.forEach((node, index) => map[node.parentId + "|" + node.id] = index);

      // Retrieve all root concepts.
      let conceptsTree = data.filter(node => node.parentId === "");

      // For each root concepts, add recursively sub concepts.
      conceptsTree.forEach((root) => this.addSubConcepts(data, map, root));

      return conceptsTree;
    },

    /**
     * Add sub concepts recursively with additional data.
     *
     * @param data
     * @param map
     * @param concept
     */
    addSubConcepts(data, map, concept) {

      // Generate an unique identifier.
      concept.UUID = this.generateUniqueId(concept.id, concept.parentId);

      // Add the current UUID to define the open concepts path.
      this.store.openConceptsPath.push(concept.UUID);

      // Only for the last concept. Case of concepts with sub concepts.
      if (concept.id === this.store.conceptDetail.id) {
        this.store.openConceptsPath.push(concept.UUID); // Add the current UUID to define the open concepts path.
        this.activeConcepts.push(concept.UUID); // Define active concepts.
      }

      // Get props for concepts that are truncated by the limit.
      concept.children?.forEach(subConcept => {

        if (!Object.prototype.hasOwnProperty.call(subConcept, "props")) {

          let currentConcept = data.filter(node => (subConcept.parentId === node.parentId && subConcept.id === node.id));

          if (currentConcept?.length > 0 && currentConcept[0]?.props) {
            subConcept.props = currentConcept[0].props;
          }
        }
      });

      concept.children?.forEach(node => {

        // Generate an unique identifier.
        node.UUID = this.generateUniqueId(node.id, concept.id);

        // Only for the last concept. Case of concepts without sub concepts.
        if (node.id === this.store.conceptDetail.id) {
          this.store.openConceptsPath.push(node.UUID); // Add the current UUID to define the open concepts path.
          this.activeConcepts.push(node.UUID); // Define active concepts.
        }

        // Get all children concepts of the current one.
        let subConcepts = data.filter(subNode => subNode.parentId === node.id);

        let subConceptsWithData = data[map[node.parentId + "|" + node.id]]?.children || [];
        let mapSubConceptsWithData = new Set(subConceptsWithData.map(el => el.parentId + "|" + el.id));

        // Merge concepts without duplicate.
        let subConceptsWithoutDuplicate = [...subConceptsWithData, ...subConcepts.filter(el => !mapSubConceptsWithData.has(el.parentId + "|" + el.id))];

        if (subConceptsWithoutDuplicate.length) {
          // JSON hack to avoid foreach to keep in memory path
          node.children = JSON.parse(JSON.stringify(subConceptsWithoutDuplicate));

          // Add sub concepts recursively.
          this.addSubConcepts(data, map, node);
        }
      });
    },

    /**
     * Generate UUID in order to avoid issue with unique constraint on treeview component.
     * @param conceptId
     * @returns {string}
     */
    generateUniqueId(conceptId, parentUri) {

      // const keyUUID = parentId + "|" + conceptId;
      let UUID = String(Math.floor(Math.random() * Date.now()));

      // UUID for the parentId|conceptId has already been registered.
      // if (keyUUID in this.UUID.reverseMapping) {
      //   return this.UUID.reverseMapping[keyUUID];
      // }

      if (this.UUID.mapping[UUID]) this.generateUniqueId(conceptId, parentUri);

      this.UUID.mapping[UUID] = conceptId;

      !(conceptId in this.UUID.reverseMapping)
        ? this.UUID.reverseMapping[conceptId] = [UUID]
        : this.UUID.reverseMapping[conceptId].push(UUID);

      this.UUID.directParentUriMapping[UUID] = parentUri;

      return UUID;
    },

    /**
     * Change the concept's detail table when user click on another concept in the tree.
     */
    updateActive: async function (newActiveNode) {

      // Retrieve its concept ID by mapping.
      let conceptId = this.UUID.mapping[newActiveNode];
      ConceptsStore.setConceptId(conceptId);

      // Define the active concepts or by default set the active concept to only the new one selected.
      this.activeConcepts = this.UUID.reverseMapping[conceptId] || [newActiveNode];

      // Update URL with the concept URI of the selected concept
      if (this.store.conceptId !== this.$route.query.concept) {
        let query = {
          ...this.$route.query,
          concept: this.store.conceptId
        };

        this.$router.push({ query }).catch(() => { });
      }

      // Load the detail of the concept
      if (this.activeConcepts.length > 0) await ConceptsStore.getConceptDetail(Snomed.checkIfSnomedBrowerLicenceValid());

      if (ConceptsStore.checkUrlParameterExists('treeViewType') && this.$route.query.treeViewType === 'simple') {
        this.conceptsTreeLoading = true;
        this.conceptsTree = await this.generateConceptsTree();
        this.conceptsTreeLoading = false;
      }

      this.updateShowIncompleteNodeAlert();
    },

    /**
     * Load children concepts and append them to the right concept node.
     */
    loadChildren: async function (item) {

      await this.getChildConcepts([item.id])
        .then((childrenConcepts) => {

          item.children = childrenConcepts[item.id]?.children.map((child) => {
            return {
              ...child,
              ...{ UUID: this.generateUniqueId(child.id, item.id) }
            };
          });

          // Retrieve configuration for each concept node that has been loaded.
          item = StoreConfiguration.browseRecursivelyNestedConceptsAndAddConfiguration(item, item.pathUris);

          this.sortTreeNode(item.children, item.configuration);
        });

      // Add concept id to the open concepts path in order to open it.
      this.store.openConceptsPath.push(item.UUID);
    },

    /**
     * Scroll to the active concept.
     */
    scrollToActiveConcept: function () {

      var activeConceptElementOffsetTop = -1;
      var scrollInterval = setInterval(() => {
        let activeConceptElements = document.getElementsByClassName("v-treeview-node--active");

        if (activeConceptElements.length > 0) {
          let activeConceptEl = activeConceptElements[0];
          if (activeConceptEl.offsetTop != activeConceptElementOffsetTop) {
            activeConceptElementOffsetTop = activeConceptEl.offsetTop;
          } else {
            this.$vuetify.goTo(activeConceptEl.offsetTop - document.getElementById("concept-tree-container").offsetTop, { container: "#concept-tree-container" });
            clearInterval(scrollInterval);
          }
        }
      }, 100);
    },

    languageChanged() {
      this.conceptsTree = this.sortConceptTree(this.conceptsTree, StoreConfiguration.getConceptTreeRootConfiguration());
    },

    updateShowIncompleteNodeAlert() {
      this.showIncompleteNodeAlert = false;
      if (this.switchCompleteTree) {
        this.activeConceptsDirectParentsUri.forEach(parentUri => {
          if (this.incompleteNodes.includes(parentUri)) {
            this.showIncompleteNodeAlert = true;
          }
        });
      }
    }
  }
}
</script>

<style lang="scss">
@import "~vuetify/src/components/VTreeview/variables"; // To get SASS variable

$ans-main-color-blue: var(--v-primary-base);
$ans-main-color-red: var(--v-secondary-base);

.switch-tree .v-input__control {
  width: auto !important;
  flex-grow: 0 !important;

  .v-input--selection-controls__input {
    margin: 0 !important;
  }

  .v-input--switch__track,
  .v-input--switch__thumb {
    color: $ans-main-color-blue !important;
  }
}

#sidebar-container {
  position: sticky;
  display: flex;
  top: 64px !important;
  flex-flow: column;
  height: 100% !important;

  .height-auto {
    flex: 0 1 auto;
  }
}

#concept-tree-container {
  flex: 1 1 auto;
  overflow-y: scroll;
  flex-basis: 0;
  min-height: 60vh;

  .v-treeview-node {
    cursor: pointer;
  }

  .v-treeview-node__level {
    width: 13px !important;
    border-right: 1px solid #B4B4B4 !important;
    height: $treeview-node-height-dense !important;
    margin: 0 12px 0 0 !important;
    box-sizing: border-box !important;
  }

  .v-treeview-node--active .v-treeview-node__label {
    font-weight: bold !important;
    color: rgb(65, 113, 177) !important;
    caret-color: rgb(65, 113, 177) !important;
  }
}

#alert-children-limit {
  margin: .5rem 0;
  padding-right: 2rem;
}
</style>
